import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { PublicProduct, publicStorefrontApi } from '../../services/storefrontService';
import { useStorefront } from '../../components/Storefront/StorefrontContext';
import { ProductPrice } from '../../components/Storefront/StorefrontProductCard';
import { useStorefrontT } from '../../components/Storefront/useStorefrontT';

/** `/store/:slug/products/:id` — Detailseite mit „In den Warenkorb“ und „Jetzt buchen“. */
const StorefrontProduct: React.FC = () => {
    const { t } = useStorefrontT();
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { slug, cart, addToCart } = useStorefront();
    const [product, setProduct] = useState<PublicProduct | null>(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        let cancelled = false;
        publicStorefrontApi
            .product(slug, Number(id))
            .then((d) => !cancelled && setProduct(d.product))
            .catch(() => !cancelled && setError(true));
        return () => {
            cancelled = true;
        };
    }, [slug, id]);

    if (error) return <Alert severity="warning">{t('product.notFound')}</Alert>;
    if (!product) return <CircularProgress />;

    const inCart = cart.includes(product.id);
    return (
        <Box>
            <Button component={RouterLink} to={`/store/${slug}/catalog`} sx={{ mb: 2 }}>{t('product.back')}</Button>
            <Typography variant="h4" component="h1" gutterBottom>{product.title}</Typography>
            <Box sx={{ mb: 2 }}><ProductPrice product={product} showVat /></Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {product.accessModel === 'time_limited' && product.accessDurationDays
                    ? t('product.timeLimited', { days: product.accessDurationDays })
                    : t('product.lifetime')}
            </Typography>
            {product.description ? <Typography sx={{ mb: 3, whiteSpace: 'pre-line' }}>{product.description}</Typography> : null}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                    variant="contained"
                    onClick={() => {
                        addToCart(product.id);
                        navigate(`/store/${slug}/checkout`);
                    }}
                >
                    {t('product.buyNow')}
                </Button>
                <Button variant="outlined" disabled={inCart} onClick={() => addToCart(product.id)}>
                    {inCart ? t('product.inCart') : t('product.addToCart')}
                </Button>
            </Stack>
        </Box>
    );
};

export default StorefrontProduct;
